import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, CheckCircle, ArrowRight, Wrench, Cpu, Cog, Package, TrendingUp, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/quote-request`, formData);
      
      if (response.data.success) {
        toast.success('Quote request sent! We\'ll contact you within 24 hours.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting quote request:', error);
      toast.error('Failed to send quote request. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const services = [
    {
      icon: <Cpu className="w-8 h-8" />,
      title: "Automation Integration",
      description: "Design, program, and commission robotic cells and conveyor systems to streamline workflows and increase throughput."
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: "CNC Repair & Maintenance",
      description: "Diagnose electrical, mechanical, and control issues on mills, lathes, routers, and multi-axis machines; perform on-site repairs and preventative maintenance."
    },
    {
      icon: <Cog className="w-8 h-8" />,
      title: "Robotic Repair & Calibration",
      description: "Troubleshoot controllers, servos, end-effectors, and teach pendant programming; recalibrate and validate repeatability."
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: "Precision Fabrication",
      description: "CNC machining, sheet metal, and custom fixtures for jigs, tooling, guards, and mounts built to print or designed in-house."
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Retrofits & Upgrades",
      description: "Modernize legacy equipment with new drives, controls, HMI, and safety systems to extend service life and improve performance."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Parts Sourcing & Supply",
      description: "Fast procurement of OEM and aftermarket components, or fabricate replacements when parts are obsolete."
    }
  ];

  const benefits = [
    {
      title: "Experienced Technicians",
      description: "Certified specialists in CNC controls and industrial robotics who prioritize correct diagnostics and durable fixes."
    },
    {
      title: "Speed with Quality",
      description: "Rapid response and efficient repairs that keep your production lines running without cutting corners."
    },
    {
      title: "Local, Dependable Service",
      description: "Based in south central Pennsylvania — prompt onsite support across the greater area."
    },
    {
      title: "Cost-Conscious Solutions",
      description: "Focused on reducing total cost of ownership through targeted repairs, strategic upgrades, and robust preventive programs."
    },
    {
      title: "Turnkey Approach",
      description: "From engineering and programming to installation and validation, we handle projects from concept to completion."
    }
  ];

  const projects = [
    {
      title: "Automated Pick-and-Place Cell",
      description: "Fully automated pick-and-place cell with vision guidance for parts handling."
    },
    {
      title: "CNC Mill Retrofit",
      description: "Retrofit of a 20-year-old CNC mill with modern servo drives and an updated control package."
    },
    {
      title: "Custom Fixturing & Tooling",
      description: "Custom fixturing and tooling for high-volume runs to improve cycle time and part consistency."
    },
    {
      title: "Emergency Robotic Repair",
      description: "Emergency robotic arm repair and recalibration to restore production within 24–48 hours."
    }
  ];

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1c1b]/95 backdrop-blur-sm border-b border-[#3f4816]/50">
        <div className="container mx-auto px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="text-[#d9fb06] text-3xl tracking-normal" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}>
                Alex<span style={{ fontStyle: 'italic' }}>&amp;</span>Co
              </div>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium">Services</a>
              <a href="#benefits" className="text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium">Why Us</a>
              <a href="#projects" className="text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium">Projects</a>
              <a href="#contact" className="text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium">Contact</a>
            </nav>
            <a href="tel:7176105547" className="hidden md:flex items-center gap-2 bg-[#d9fb06] text-[#1a1c1b] px-6 py-3 rounded-full font-semibold hover:scale-105 transition-transform uppercase text-sm tracking-tight">
              <Phone className="w-4 h-4" />
              717 610 5547
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section relative min-h-screen flex items-center overflow-hidden pt-20">
        <div className="hero-background absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1713371398484-cc4e4f6a262a" 
            alt="CNC machine precision cutting metal - Alex&Co CNC repair services"
            className="hero-image object-cover w-full h-full"
          />
          <div className="hero-overlay absolute inset-0 bg-gradient-to-br from-black/90 via-black/75 to-black/40"></div>
        </div>
        
        <div className="container mx-auto px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="text-[#888680] uppercase text-sm font-semibold tracking-wider mb-4">
              CNC & Robotics Services
            </div>
            <h1 className="brand-display text-[#d9fb06] mb-6">
              AUTOMATION INTEGRATION • PRECISION FABRICATION
            </h1>
            <p className="body-large text-[#d9fb06] mb-8 max-w-2xl">
              Alex&Co delivers fast, reliable CNC and robotics services tailored to manufacturers in south central Pennsylvania and the surrounding region.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#contact" className="btn-primary bg-[#d9fb06] text-[#1a1c1b] px-8 py-4 rounded-full font-semibold hover:scale-105 transition-transform uppercase text-base inline-flex items-center gap-2">
                Request a Quote <ArrowRight className="w-5 h-5" />
              </a>
              <a href="tel:7176105547" className="btn-secondary border-2 border-[#d9fb06] text-[#d9fb06] px-8 py-4 rounded-full font-semibold hover:bg-[#d9fb06] hover:text-[#1a1c1b] transition-all uppercase text-base inline-flex items-center gap-2">
                <Phone className="w-5 h-5" />
                Emergency 24/7
              </a>
            </div>
            <div className="mt-12 flex flex-wrap gap-6 text-[#d9fb06] font-semibold">
              <div className="flex items-center gap-2 bg-black/40 px-4 py-2 rounded-full backdrop-blur-sm">
                <MapPin className="w-5 h-5 text-[#d9fb06]" />
                <span>York Springs, PA</span>
              </div>
              <div className="flex items-center gap-2 bg-black/40 px-4 py-2 rounded-full backdrop-blur-sm">
                <Clock className="w-5 h-5 text-[#d9fb06]" />
                <span>24/7 Emergency Service</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 px-8 bg-[#1a1c1b]">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="heading-2 text-[#d9fb06] mb-4">WHAT WE DO</h2>
            <p className="body-medium text-[#888680] max-w-3xl mx-auto">
              We combine deep technical expertise with practical problem-solving to minimize downtime and maximize production value.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="team-card bg-[#302f2c] border-[#3f4816] p-8 hover:bg-[#3f4816] transition-colors">
                <div className="text-[#d9fb06] mb-4">
                  {service.icon}
                </div>
                <h3 className="heading-5 text-[#d9fb06] mb-3">{service.title}</h3>
                <p className="body-small text-[#888680]">{service.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-24 px-8 bg-[#302f2c] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1647427060118-4911c9821b82" 
            alt="Industrial automation"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="heading-2 text-[#d9fb06] mb-4">WHY CHOOSE ALEX&CO</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-[#d9fb06]" />
                </div>
                <div>
                  <h3 className="heading-6 text-[#d9fb06] mb-2">{benefit.title}</h3>
                  <p className="body-small text-[#888680]">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-24 px-8 bg-[#1a1c1b]">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="heading-2 text-[#d9fb06] mb-4">TYPICAL PROJECTS</h2>
            <p className="body-medium text-[#888680] max-w-3xl mx-auto">
              From automation cells to emergency repairs, we handle projects from concept to completion.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {projects.map((project, index) => (
              <Card key={index} className="event-card bg-[#3f4816] p-8 border-[#3f4816]">
                <h3 className="heading-5 text-[#d9fb06] mb-3">{project.title}</h3>
                <p className="body-small text-[#888680]">{project.description}</p>
              </Card>
            ))}
          </div>

          <div className="relative rounded-lg overflow-hidden h-96">
            <img 
              src="https://images.unsplash.com/photo-1711418235199-171c8ecb9d12" 
              alt="Precision CNC manufacturing and metal fabrication by Alex&Co"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1c1b] via-transparent to-transparent"></div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-8 bg-[#302f2c]">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="heading-2 text-[#d9fb06] mb-4">GET STARTED</h2>
            <p className="body-medium text-[#888680] max-w-3xl mx-auto">
              Contact Alex&Co for a site assessment, machine audit, or project quote. We'll provide clear diagnostics, realistic timelines, and cost-effective recommendations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h3 className="heading-5 text-[#d9fb06] mb-6">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <Phone className="w-6 h-6 text-[#d9fb06] flex-shrink-0 mt-1" />
                    <div>
                      <div className="text-[#d9fb06] font-semibold mb-1">Phone</div>
                      <a href="tel:7176105547" className="text-[#888680] hover:text-[#d9fb06] transition-colors text-lg">
                        717 610 5547
                      </a>
                      <div className="text-[#888680] text-sm mt-1">24/7 Emergency Service</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <Mail className="w-6 h-6 text-[#d9fb06] flex-shrink-0 mt-1" />
                    <div>
                      <div className="text-[#d9fb06] font-semibold mb-1">Email</div>
                      <a href="mailto:service@alexco.tech" className="text-[#888680] hover:text-[#d9fb06] transition-colors">
                        service@alexco.tech
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-[#d9fb06] flex-shrink-0 mt-1" />
                    <div>
                      <div className="text-[#d9fb06] font-semibold mb-1">Location</div>
                      <div className="text-[#888680]">
                        York Springs, PA
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#3f4816] p-6 rounded-lg">
                <div className="text-[#d9fb06] font-semibold mb-2 flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  Emergency Service Available
                </div>
                <p className="text-[#888680] text-sm">
                  Production down? Call us 24/7 for rapid response. We prioritize emergency repairs to get your operations back online fast.
                </p>
              </div>
            </div>

            {/* Quote Form */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-[#d9fb06] text-sm font-semibold mb-2 block uppercase tracking-wide">Name *</label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="bg-[#1a1c1b] border-[#3f4816] text-[#d9fb06] focus:border-[#d9fb06] rounded-lg"
                    placeholder="Your name"
                  />
                </div>
                
                <div>
                  <label className="text-[#d9fb06] text-sm font-semibold mb-2 block uppercase tracking-wide">Email *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="bg-[#1a1c1b] border-[#3f4816] text-[#d9fb06] focus:border-[#d9fb06] rounded-lg"
                    placeholder="your@email.com"
                  />
                </div>
                
                <div>
                  <label className="text-[#d9fb06] text-sm font-semibold mb-2 block uppercase tracking-wide">Phone *</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="bg-[#1a1c1b] border-[#3f4816] text-[#d9fb06] focus:border-[#d9fb06] rounded-lg"
                    placeholder="(555) 123-4567"
                  />
                </div>
                
                <div>
                  <label className="text-[#d9fb06] text-sm font-semibold mb-2 block uppercase tracking-wide">Company</label>
                  <Input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="bg-[#1a1c1b] border-[#3f4816] text-[#d9fb06] focus:border-[#d9fb06] rounded-lg"
                    placeholder="Your company name"
                  />
                </div>
                
                <div>
                  <label className="text-[#d9fb06] text-sm font-semibold mb-2 block uppercase tracking-wide">Service Needed</label>
                  <Input
                    type="text"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="bg-[#1a1c1b] border-[#3f4816] text-[#d9fb06] focus:border-[#d9fb06] rounded-lg"
                    placeholder="e.g., CNC Repair, Automation Integration"
                  />
                </div>
                
                <div>
                  <label className="text-[#d9fb06] text-sm font-semibold mb-2 block uppercase tracking-wide">Message *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="bg-[#1a1c1b] border-[#3f4816] text-[#d9fb06] focus:border-[#d9fb06] rounded-lg resize-none"
                    placeholder="Tell us about your project or repair needs..."
                  />
                </div>
                
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full py-6 font-semibold uppercase tracking-wide text-base hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Request Quote'} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1c1b] border-t border-[#3f4816] py-12 px-8">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="text-[#d9fb06] text-3xl tracking-normal mb-4" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}>
                Alex<span style={{ fontStyle: 'italic' }}>&amp;</span>Co
              </div>
              <p className="text-[#888680] text-sm">
                Fast, reliable CNC and robotics services for manufacturers in south central Pennsylvania.
              </p>
            </div>
            
            <div>
              <h4 className="text-[#d9fb06] font-semibold mb-4 uppercase tracking-wide text-sm">Quick Links</h4>
              <div className="space-y-2">
                <a href="#services" className="block text-[#888680] hover:text-[#d9fb06] transition-colors text-sm">Services</a>
                <a href="#benefits" className="block text-[#888680] hover:text-[#d9fb06] transition-colors text-sm">Why Choose Us</a>
                <a href="#projects" className="block text-[#888680] hover:text-[#d9fb06] transition-colors text-sm">Projects</a>
                <a href="#contact" className="block text-[#888680] hover:text-[#d9fb06] transition-colors text-sm">Contact</a>
              </div>
            </div>
            
            <div>
              <h4 className="text-[#d9fb06] font-semibold mb-4 uppercase tracking-wide text-sm">Contact</h4>
              <div className="space-y-2 text-sm text-[#888680]">
                <div>York Springs, PA</div>
                <a href="tel:7176105547" className="block hover:text-[#d9fb06] transition-colors">717 610 5547</a>
                <a href="mailto:service@alexco.tech" className="block hover:text-[#d9fb06] transition-colors">service@alexco.tech</a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-[#3f4816] pt-8 text-center text-[#888680] text-sm">
            <p>&copy; {new Date().getFullYear()} Alex&Co. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
